// src/components/issues/IssueFormModal.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Textarea, Select, VStack, FormErrorMessage,
  useColorModeValue, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, 
  NumberDecrementStepper, Alert, AlertIcon, AlertTitle, AlertDescription, Spinner, Box, Text, 
  HStack, useToast, Icon, Badge, Image, IconButton, Divider, SimpleGrid, Progress, Tooltip,
  InputGroup, InputRightElement, Collapse, useDisclosure, Slide, ScaleFade
} from '@chakra-ui/react';
import { 
  MdMyLocation, MdImage, MdAdd, MdClose, MdWarning, MdInfo, MdCheckCircle,
  MdExpandMore, MdExpandLess, MdLink, MdSave
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import * as issueService from '../../services/issueService';
import logger from '../../utils/debugLogger.js';

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);

const DEBUG_CONTEXT = '[IssueFormModal]';

const logDebug = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`${timestamp} ${DEBUG_CONTEXT} ${message}`, data || '');
  logger.log(`${DEBUG_CONTEXT} ${message}`, data);
};

const logError = (message, error) => {
  const timestamp = new Date().toISOString();
  console.error(`${timestamp} ${DEBUG_CONTEXT} ${message}`, error);
  logger.error(`${DEBUG_CONTEXT} ${message}`, error);
};

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: 'green', description: 'Minor issue, no immediate action needed' },
  { value: 'medium', label: 'Medium', color: 'yellow', description: 'Moderate issue, requires attention soon' },
  { value: 'high', label: 'High', color: 'orange', description: 'Significant issue, requires prompt action' },
  { value: 'critical', label: 'Critical', color: 'red', description: 'Emergency, requires immediate response' }
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const IssueFormModal = ({ isOpen, onClose, onSubmit, initialData, isLoading, issueStatuses, issueTypes }) => {
  const defaultFormData = useMemo(() => ({
    latitude: '',
    longitude: '',
    issue_type: '',
    description: '',
    status: 'open',
    work_order_id: '',
    severity: 'medium',
  }), []);

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [issueImage, setIssueImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [nearbyIssues, setNearbyIssues] = useState([]);
  const [isCheckingNearby, setIsCheckingNearby] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [hasModifiedCoordinates, setHasModifiedCoordinates] = useState(false);
  const fileInputRef = useRef(null);
  const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle } = useDisclosure();
  
  const toast = useToast();
  
  const modalBg = useColorModeValue("white", "navy.800");
  const inputBg = useColorModeValue("gray.50", "navy.700");
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const advancedBg = useColorModeValue("gray.50", "navy.900");
  const nearbyIssueBg = useColorModeValue('white', 'navy.700');
  const gradientBg = useColorModeValue(
    'linear(to-r, blue.400, purple.500)',
    'linear(to-r, blue.600, purple.700)'
  );

  useEffect(() => {
    if (isOpen) {
      logDebug('Modal opened', { hasInitialData: !!initialData });
      
      if (initialData) {
        const loadedData = {
          id: initialData.id || undefined,
          latitude: initialData.latitude?.toString() || '',
          longitude: initialData.longitude?.toString() || '',
          issue_type: initialData.issue_type || '',
          description: initialData.description || '',
          status: initialData.status || 'open',
          work_order_id: initialData.work_order_id || '',
          severity: initialData.severity || 'medium',
        };
        
        logDebug('Loading initial data', { loadedData });
        setFormData(loadedData);
      } else {
        setFormData(defaultFormData);
      }
      
      setErrors({});
      setIssueImage(null);
      setImagePreview(null);
      setNearbyIssues([]);
      setIsCheckingNearby(false);
      setHasModifiedCoordinates(false);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [initialData, isOpen, defaultFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    logDebug('Field changed', { name, value: value.substring(0, 50) });
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      logDebug('File selection cancelled');
      return;
    }

    logDebug('File selected', { 
      name: file.name, 
      size: file.size, 
      type: file.type 
    });

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      logError('Invalid file type', { type: file.type });
      
      toast({
        title: 'Invalid File Type',
        description: 'Please select a JPEG, PNG, or WebP image.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      logError('File too large', { size: file.size, maxSize: MAX_FILE_SIZE });
      
      toast({
        title: 'File Too Large',
        description: `Image must be smaller than ${MAX_FILE_SIZE / 1024 / 1024}MB.`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIssueImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      logDebug('Image preview generated');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    logDebug('Removing selected image');
    
    setIssueImage(null);
    setImagePreview(null);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleNumberChange = (name, valueAsString) => {
    logDebug('Number field changed', { name, value: valueAsString });
    
    setFormData(prev => ({ ...prev, [name]: valueAsString }));
    
    if (name === 'latitude' || name === 'longitude') {
      setHasModifiedCoordinates(true);
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleGetCurrentLocation = () => {
    logDebug('Requesting current location');
    
    if (!navigator.geolocation) {
      logError('Geolocation not supported');
      
      toast({
        title: 'Geolocation Not Supported',
        description: 'Your browser does not support geolocation.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(8);
        const lng = position.coords.longitude.toFixed(8);
        
        logDebug('Location obtained', { lat, lng, accuracy: position.coords.accuracy });

        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));

        setHasModifiedCoordinates(true);
        setIsGettingLocation(false);

        toast({
          title: 'Location Obtained',
          description: `Coordinates set to ${lat}, ${lng}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      },
      (error) => {
        logError('Geolocation error', error);
        
        setIsGettingLocation(false);

        let errorMessage = 'Unable to get your location.';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Location access was denied. Please enable location permissions.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'Location information is unavailable.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Location request timed out.';
        }

        toast({
          title: 'Location Error',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const validateForm = () => {
    const newErrors = {};
    const lat = parseFloat(formData.latitude);
    const lon = parseFloat(formData.longitude);

    if (formData.latitude === '' || isNaN(lat)) {
      newErrors.latitude = 'Valid latitude is required.';
    } else if (lat < -90 || lat > 90) {
      newErrors.latitude = 'Latitude must be between -90 and 90.';
    }
    
    if (formData.longitude === '' || isNaN(lon)) {
      newErrors.longitude = 'Valid longitude is required.';
    } else if (lon < -180 || lon > 180) {
      newErrors.longitude = 'Longitude must be between -180 and 180.';
    }

    if (!formData.issue_type) {
      newErrors.issue_type = 'Issue type is required.';
    }
    
    if (!formData.status) {
      newErrors.status = 'Status is required.';
    }

    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters.';
    }
    
    logDebug('Form validation', { 
      isValid: Object.keys(newErrors).length === 0, 
      errors: newErrors 
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitId = `SUBMIT_${Date.now()}`;
    logDebug('Form submission initiated', { submitId, isEdit: !!initialData });
    
    if (!validateForm()) {
      logDebug('Validation failed', { submitId });
      return;
    }

    let dataToSubmit;
    const cleanWorkOrderId = formData.work_order_id || null;

    if (!initialData) {
      dataToSubmit = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key !== 'work_order_id' && formData[key] !== null && formData[key] !== undefined) {
          dataToSubmit.append(key, formData[key]);
        }
      });

      if (cleanWorkOrderId) {
        dataToSubmit.append('work_order_id', cleanWorkOrderId);
      }

      if (issueImage) {
        dataToSubmit.append('issueImage', issueImage);
        logDebug('Image attached', { submitId, fileName: issueImage.name });
      }

      logDebug('FormData prepared for creation', { submitId });
    } else {
      dataToSubmit = {
        issue_type: formData.issue_type,
        description: formData.description,
        status: formData.status,
        work_order_id: cleanWorkOrderId,
        severity: formData.severity,
      };
      
      logDebug('JSON data prepared for update', { submitId, issueId: initialData.id });
    }
    
    onSubmit(dataToSubmit);
  };

  const checkDuplicates = useCallback(async (lat, lng) => {
    if (lat && lng && !initialData) {
      const checkId = `CHECK_${Date.now()}`;
      logDebug('Checking for nearby issues', { checkId, lat, lng });
      
      setIsCheckingNearby(true);
      
      try {
        const issues = await issueService.findNearbyIssues(lat, lng, 100, 24);
        
        logDebug('Nearby check complete', { 
          checkId, 
          foundCount: issues?.length || 0 
        });
        
        setNearbyIssues(issues || []);
      } catch (err) {
        logError('Error checking nearby issues', err);
      }
      
      setIsCheckingNearby(false);
    }
  }, [initialData]);

  useEffect(() => {
    const lat = parseFloat(formData.latitude);
    const lon = parseFloat(formData.longitude);
    
    if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
      const handler = setTimeout(() => checkDuplicates(lat, lon), 1000);
      return () => clearTimeout(handler);
    } else {
      setNearbyIssues([]);
    }
  }, [formData.latitude, formData.longitude, checkDuplicates]);

  const handleLinkToIssue = async (existingIssueId) => {
    const linkId = `LINK_${Date.now()}`;
    logDebug('Linking to existing issue', { linkId, existingIssueId });
    
    const newDescription = formData.description;

    if (!newDescription || newDescription.trim() === '') {
      logDebug('Link cancelled - no description', { linkId });
      
      toast({
        title: 'Description Required',
        description: 'Please enter a description to link it as a note.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      await issueService.linkIssue(existingIssueId, { description: newDescription });
      
      logDebug('Successfully linked to issue', { linkId, existingIssueId });
      
      toast({
        title: 'Report Linked!',
        description: `Your notes were successfully added to issue #${existingIssueId}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onClose();
    } catch (err) {
      logError('Failed to link issue', err);
      
      toast({
        title: 'Linking Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formProgress = useMemo(() => {
    const requiredFields = ['latitude', 'longitude', 'issue_type', 'status', 'description'];
    const filledFields = requiredFields.filter(field => {
      if (field === 'description') {
        return formData[field] && formData[field].trim().length >= 10;
      }
      return formData[field] && formData[field] !== '';
    });
    
    return (filledFields.length / requiredFields.length) * 100;
  }, [formData]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="xl" 
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
      <ModalContent 
        as="form" 
        onSubmit={handleSubmit} 
        bg={modalBg} 
        maxH="95vh"
        borderRadius="2xl"
        overflow="hidden"
      >
        {/* Animated gradient header */}
        <MotionBox
          h="4px"
          bgGradient={gradientBg}
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            backgroundSize: '200% 200%'
          }}
        />

        <ModalHeader borderBottomWidth="1px">
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={initialData ? MdSave : MdAdd} boxSize={5} color="blue.500" />
                <Text fontWeight="800">
                  {initialData ? 'Edit Issue Details' : 'Report New Issue'}
                </Text>
              </HStack>
              {!initialData && (
                <ScaleFade in={true}>
                  <Badge colorScheme={formProgress === 100 ? 'green' : 'blue'} fontSize="sm" px={3} py={1} borderRadius="full">
                    {formProgress.toFixed(0)}% Complete
                  </Badge>
                </ScaleFade>
              )}
            </HStack>
            {!initialData && (
              <MotionBox
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5 }}
                style={{ transformOrigin: "left" }}
              >
                <Progress 
                  value={formProgress} 
                  size="xs" 
                  colorScheme={formProgress === 100 ? 'green' : 'blue'} 
                  borderRadius="full"
                  hasStripe
                  isAnimated
                />
              </MotionBox>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px={6} py={6} overflowY="auto">
          <VStack spacing={5} align="stretch">
            <ScaleFade in={true} initialScale={0.95}>
              <Alert status="info" borderRadius="xl" variant="left-accent">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm" fontWeight="700">Location Information</AlertTitle>
                  <AlertDescription fontSize="xs">
                    {initialData 
                      ? 'Coordinates cannot be changed for existing issues.'
                      : 'Use the location button to auto-fill your current coordinates.'}
                  </AlertDescription>
                </Box>
              </Alert>
            </ScaleFade>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.latitude}>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Latitude</FormLabel>
                <NumberInput 
                  name="latitude" 
                  value={formData.latitude}
                  onChange={(valueString) => handleNumberChange('latitude', valueString)}
                  precision={8} 
                  step={0.000001}
                  min={-90} 
                  max={90}
                  isReadOnly={!!initialData}
                >
                  <NumberInputField 
                    bg={inputBg} 
                    placeholder="e.g., 25.0775" 
                    fontSize="sm"
                    borderRadius="lg"
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                {errors.latitude && <FormErrorMessage fontSize="xs">{errors.latitude}</FormErrorMessage>}
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.longitude}>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Longitude</FormLabel>
                <InputGroup>
                  <NumberInput 
                    name="longitude" 
                    value={formData.longitude}
                    onChange={(valueString) => handleNumberChange('longitude', valueString)}
                    precision={8} 
                    step={0.000001}
                    min={-180} 
                    max={180}
                    isReadOnly={!!initialData}
                    w="100%"
                  >
                    <NumberInputField 
                      bg={inputBg} 
                      placeholder="e.g., -77.3398" 
                      fontSize="sm"
                      borderRadius="lg"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  {!initialData && (
                    <InputRightElement width="3rem">
                      <Tooltip label="Get current location" placement="top">
                        <IconButton
                          icon={<Icon as={MdMyLocation} />}
                          size="sm"
                          onClick={handleGetCurrentLocation}
                          isLoading={isGettingLocation}
                          aria-label="Get location"
                          variant="ghost"
                          colorScheme="blue"
                        />
                      </Tooltip>
                    </InputRightElement>
                  )}
                </InputGroup>
                {errors.longitude && <FormErrorMessage fontSize="xs">{errors.longitude}</FormErrorMessage>}
              </FormControl>
            </SimpleGrid>

            {isCheckingNearby && (
              <MotionBox
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <HStack spacing={2} color="blue.500">
                  <Spinner size="sm" />
                  <Text fontSize="sm">Checking for nearby issues...</Text>
                </HStack>
              </MotionBox>
            )}

            <AnimatePresence>
              {nearbyIssues.length > 0 && (
                <MotionBox
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert status="warning" borderRadius="xl" flexDirection="column" alignItems="start" boxShadow="md">
                    <HStack>
                      <AlertIcon />
                      <AlertTitle fontSize="sm" fontWeight="700">
                        <Icon as={MdWarning} mr={1} />
                        Potential Duplicates Found!
                      </AlertTitle>
                    </HStack>
                    <AlertDescription width="100%" mt={3}>
                      <Text fontSize="xs" mb={3}>
                        Found {nearbyIssues.length} similar report(s) within 100m. Please review before creating a new issue.
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {nearbyIssues.slice(0, 3).map(issue => (
                          <MotionBox
                            key={issue.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Box 
                              p={3} 
                              borderWidth="1px" 
                              borderRadius="lg" 
                              bg={nearbyIssueBg}
                              boxShadow="sm"
                              _hover={{ boxShadow: 'md' }}
                              transition="all 0.2s"
                            >
                              <HStack justify="space-between" align="start">
                                <Box flex={1}>
                                  <HStack mb={2} flexWrap="wrap">
                                    <Badge colorScheme="blue" fontSize="xs">#{issue.id}</Badge>
                                    <Badge colorScheme="purple" fontSize="xs">{issue.issue_type}</Badge>
                                    {issue.severity && (
                                      <Badge colorScheme={
                                        issue.severity === 'critical' ? 'red' :
                                        issue.severity === 'high' ? 'orange' :
                                        issue.severity === 'medium' ? 'yellow' : 'green'
                                      } fontSize="xs">
                                        {issue.severity}
                                      </Badge>
                                    )}
                                  </HStack>
                                  <Text fontSize="xs" noOfLines={2} color="gray.600">
                                    {issue.description}
                                  </Text>
                                  {issue.distance_meters && (
                                    <Text fontSize="2xs" color="gray.500" mt={1}>
                                      {issue.distance_meters}m away
                                    </Text>
                                  )}
                                </Box>
                                <Button 
                                  size="xs" 
                                  colorScheme="orange"
                                  leftIcon={<Icon as={MdLink} />}
                                  onClick={() => handleLinkToIssue(issue.id)}
                                  borderRadius="lg"
                                >
                                  Link
                                </Button>
                              </HStack>
                            </Box>
                          </MotionBox>
                        ))}
                        {nearbyIssues.length > 3 && (
                          <Text fontSize="xs" color="gray.500" textAlign="center">
                            + {nearbyIssues.length - 3} more nearby issue(s)
                          </Text>
                        )}
                      </VStack>
                    </AlertDescription>
                  </Alert>
                </MotionBox>
              )}
            </AnimatePresence>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.issue_type}>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Issue Type</FormLabel>
                <Select
                  name="issue_type"
                  placeholder="Select type"
                  value={formData.issue_type}
                  onChange={handleChange}
                  bg={inputBg}
                  fontSize="sm"
                  borderRadius="lg"
                >
                  {(issueTypes || []).map(type => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ').toUpperCase()}
                    </option>
                  ))}
                </Select>
                {errors.issue_type && <FormErrorMessage fontSize="xs">{errors.issue_type}</FormErrorMessage>}
              </FormControl>

              <FormControl isRequired>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Severity</FormLabel>
                <Select
                  name="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  bg={inputBg}
                  fontSize="sm"
                  borderRadius="lg"
                >
                  {SEVERITY_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            <AnimatePresence>
              {formData.severity && (
                <MotionBox
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Alert 
                    status={
                      formData.severity === 'critical' ? 'error' :
                      formData.severity === 'high' ? 'warning' : 'info'
                    }
                    variant="left-accent"
                    fontSize="xs"
                    borderRadius="lg"
                  >
                    <AlertIcon />
                    {SEVERITY_LEVELS.find(l => l.value === formData.severity)?.description}
                  </Alert>
                </MotionBox>
              )}
            </AnimatePresence>

            <FormControl isRequired isInvalid={!!errors.description}>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="600">
                Description ({formData.description.length} characters)
              </FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                bg={inputBg}
                placeholder="Provide a detailed description (minimum 10 characters)..."
                rows={4}
                fontSize="sm"
                borderRadius="lg"
              />
              {errors.description && <FormErrorMessage fontSize="xs">{errors.description}</FormErrorMessage>}
            </FormControl>

            {!initialData && (
              <FormControl>
                <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Upload Image (Optional)</FormLabel>
                {imagePreview ? (
                  <MotionBox
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    position="relative"
                  >
                    <Image 
                      src={imagePreview} 
                      alt="Preview" 
                      maxH="200px" 
                      borderRadius="xl"
                      objectFit="cover"
                      boxShadow="md"
                    />
                    <IconButton
                      icon={<Icon as={MdClose} />}
                      size="sm"
                      colorScheme="red"
                      position="absolute"
                      top={2}
                      right={2}
                      onClick={handleRemoveImage}
                      aria-label="Remove image"
                      borderRadius="lg"
                    />
                    <Badge position="absolute" bottom={2} left={2} colorScheme="green" borderRadius="md">
                      <Icon as={MdCheckCircle} mr={1} />
                      {(issueImage.size / 1024).toFixed(1)} KB
                    </Badge>
                  </MotionBox>
                ) : (
                  <Button
                    leftIcon={<Icon as={MdImage} />}
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    w="100%"
                    size="md"
                    borderRadius="lg"
                    borderStyle="dashed"
                    borderWidth="2px"
                    _hover={{ borderColor: 'blue.500', bg: 'blue.50' }}
                  >
                    Choose Image
                  </Button>
                )}
                <Input
                  ref={fileInputRef}
                  type="file"
                  name="issueImage"
                  onChange={handleFileChange}
                  accept="image/*"
                  display="none"
                />
                <Text fontSize="2xs" color="gray.500" mt={2}>
                  Max size: 5MB. Formats: JPEG, PNG, WebP
                </Text>
              </FormControl>
            )}

            <Box>
              <Button
                size="sm"
                variant="ghost"
                onClick={onAdvancedToggle}
                rightIcon={<Icon as={isAdvancedOpen ? MdExpandLess : MdExpandMore} />}
                w="100%"
                borderRadius="lg"
              >
                Advanced Options
              </Button>
              
              <Collapse in={isAdvancedOpen} animateOpacity>
                <VStack 
                  spacing={4} 
                  align="stretch" 
                  mt={3} 
                  p={4} 
                  bg={advancedBg} 
                  borderRadius="xl"
                  borderWidth="1px"
                >
                  <FormControl isRequired isInvalid={!!errors.status}>
                    <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Status</FormLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      bg={inputBg}
                      fontSize="sm"
                      borderRadius="lg"
                    >
                      {(issueStatuses || []).map(stat => (
                        <option key={stat} value={stat}>
                          {stat.replace(/_/g, ' ').toUpperCase()}
                        </option>
                      ))}
                    </Select>
                    {errors.status && <FormErrorMessage fontSize="xs">{errors.status}</FormErrorMessage>}
                  </FormControl>

                  <FormControl>
                    <FormLabel color={labelColor} fontSize="sm" fontWeight="600">Link to Work Order (Optional)</FormLabel>
                    <Input
                      name="work_order_id"
                      value={formData.work_order_id}
                      onChange={handleChange}
                      bg={inputBg}
                      placeholder="Enter work order ID"
                      fontSize="sm"
                      borderRadius="lg"
                    />
                    <Text fontSize="2xs" color="gray.500" mt={2}>
                      Link this issue to an existing work order
                    </Text>
                  </FormControl>
                </VStack>
              </Collapse>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" bg={useColorModeValue('gray.50', 'gray.900')}>
          <HStack spacing={3} w="100%" justify="space-between">
            <Button variant="ghost" onClick={onClose} size="md" borderRadius="lg">
              Cancel
            </Button>
            <HStack>
              {nearbyIssues.length > 0 && !initialData && (
                <Tooltip label="Similar issues found nearby">
                  <Icon as={MdInfo} color="orange.500" boxSize={5} />
                </Tooltip>
              )}
              <Button 
                type="submit" 
                colorScheme={nearbyIssues.length > 0 && !initialData ? "yellow" : "blue"} 
                isLoading={isLoading}
                leftIcon={<Icon as={MdSave} />}
                isDisabled={formProgress < 100 && !initialData}
                size="md"
                borderRadius="lg"
                boxShadow="md"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                {initialData 
                  ? 'Save Changes' 
                  : (nearbyIssues.length > 0 ? 'Create Anyway' : 'Create Issue')}
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default IssueFormModal;